/**
 * Hook for fetching and updating account settings (NLV).
 * Uses SWR for caching with optimistic updates on save.
 */

import { useCallback, useState } from 'react';
import useSWR from 'swr';
import { fetchAccountSettings, updateAccountNlv, type AccountSettings } from '@/services/api';

/**
 * Fetch and manage account settings for a given account.
 */
export function useAccountSettings(accountId: string | null) {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data, error, isLoading, mutate } = useSWR<AccountSettings>(
    accountId ? ['account-settings', accountId] : null,
    () => (accountId ? fetchAccountSettings(accountId) : Promise.resolve({ nlv: null, nlv_updated_at: null })),
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000,
    }
  );

  const saveNlv = useCallback(
    async (nlv: number) => {
      if (!accountId) return;
      setIsSaving(true);
      setSaveError(null);

      try {
        const updated = await updateAccountNlv(accountId, nlv);
        await mutate(updated, false);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save NLV';
        setSaveError(message);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [accountId, mutate]
  );

  return {
    settings: data ?? { nlv: null, nlv_updated_at: null },
    error: error?.message ?? null,
    isLoading,
    isSaving,
    saveError,
    saveNlv,
    refresh: mutate,
  };
}
